import type { UIMessage } from "ai";
import { z } from "zod";

import { assistantImageGenerationAspectRatioSchema } from "@runcheetah/shared";

const brandedImageGenerationPreviewSchema = z.object({
  render: z.object({ component: z.literal("branded_image_generation_confirmation") }),
  summary: z.string().trim().min(1).max(600).nullable().optional(),
  prompt: z.string().trim().min(5).max(4_000),
  variantCount: z.number().int().min(1).max(4),
  aspectRatio: assistantImageGenerationAspectRatioSchema,
  sourceGeneratedAssetId: z.uuid().nullable().optional(),
});

export type AssistantBrandedImageGenerationPreviewReference = {
  id: string;
  summary: string | null;
  request: {
    prompt: string;
    variantCount: number;
    aspectRatio: z.infer<typeof assistantImageGenerationAspectRatioSchema>;
    sourceGeneratedAssetId?: string;
  };
};

export function getAssistantBrandedImageGenerationPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantBrandedImageGenerationPreviewReference[] {
  const references: AssistantBrandedImageGenerationPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.state !== "output-available" ||
      (candidate.type !== "tool-generate_branded_image" &&
        candidate.type !== "tool-edit_branded_image")
    ) {
      continue;
    }

    const reference = parseAssistantBrandedImageGenerationPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `branded-image-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantBrandedImageGenerationProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

function parseAssistantBrandedImageGenerationPreviewReference(
  value: unknown,
  id: string,
): AssistantBrandedImageGenerationPreviewReference | null {
  const parsed = brandedImageGenerationPreviewSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    id,
    summary: parsed.data.summary?.trim() || null,
    request: {
      prompt: parsed.data.prompt,
      variantCount: parsed.data.variantCount,
      aspectRatio: parsed.data.aspectRatio,
      ...(parsed.data.sourceGeneratedAssetId
        ? { sourceGeneratedAssetId: parsed.data.sourceGeneratedAssetId }
        : {}),
    },
  };
}
